import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Warn early if the backend URL wasn't provided at build time — this is a
// common cause of "white page after register/login" in deployments.
if (!BACKEND_URL && typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.error(
    "[config] REACT_APP_BACKEND_URL is missing. API calls will fail until it is set at build time."
  );
}

// -------- Per-tab session token storage --------
// Kept in sessionStorage (isolated per browser tab) instead of an httpOnly
// cookie. This lets multiple accounts stay logged in simultaneously in
// different tabs of the same browser without interfering with each other.
export const TOKEN_KEY = "revo.session.token";

export function getStoredToken() {
  try { return sessionStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}
export function setStoredToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode / disabled storage */ }
}

// NOTE: we intentionally do NOT set `withCredentials: true`. Cookies are
// shared across tabs of the same origin and would defeat per-tab isolation.
// Auth flows entirely through the `Authorization: Bearer` header.
export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Clear the tab's token whenever the server tells us it is invalid/expired.
// On 402 (free trial expired or admin-disabled) redirect to /subscribe with a
// friendly reason instead of letting raw error bubbles up to the UI.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    if (status === 401) setStoredToken(null);
    if (status === 402) {
      // Only redirect if we're not already on the subscribe page.
      try {
        const path = typeof window !== "undefined" ? window.location.pathname : "";
        if (path && !path.startsWith("/subscribe") && !path.startsWith("/login") && !path.startsWith("/register")) {
          window.location.assign("/subscribe?reason=trial_expired");
        }
      } catch (_) { /* ignore */ }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
